import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CampaignIcon from "@mui/icons-material/Campaign";
import "../assets/style/SiteBanners.css";

const API = process.env.REACT_APP_BACKEND_ADDRESS;

/**
 * Admin-authored announcement bars for the public site.
 *
 * Fetches the currently scheduled banners from `GET /banners/active` (no auth)
 * and stacks them as a fixed bar pinned under the site header — below the
 * `PendingSignupsBanner` and `PromoBanner` when those are present. Each banner
 * carries its own colour, optional call-to-action link and, unless the admin
 * turned it off, a close button.
 *
 * Dismissal is deliberately kept in component state only — nothing is persisted
 * — so closing a banner clears it from the current view but it comes back on
 * the next page load.
 *
 * Renders nothing when nothing is scheduled or everything was dismissed.
 */
const SiteBanners = () => {
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState({});
  const [topOffset, setTopOffset] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/banners/active`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setBanners(Array.isArray(data) ? data : []);
      } catch (e) {
        // Announcements are non-critical: fail silently.
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Pin below the fixed header and any banner already stacked above us,
  // tracking their heights across breakpoints and dynamic mounts.
  const measure = useCallback(() => {
    const header = document.getElementById("header");
    let offset = header ? header.offsetHeight : 0;
    [".pending-signups-banner", ".promo-banner"].forEach((selector) => {
      const el = document.querySelector(selector);
      if (el) offset += el.offsetHeight;
    });
    setTopOffset(offset);
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    // The banners above us mount asynchronously after their data loads; observe
    // the DOM so we re-pin underneath them when they appear/disappear.
    const observer = new MutationObserver(measure);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      window.removeEventListener("resize", measure);
      observer.disconnect();
    };
  }, [measure]);

  const handleDismiss = (id) => {
    setDismissed((prev) => ({ ...prev, [id]: true }));
  };

  const visible = banners.filter((banner) => !dismissed[banner.id]);
  if (visible.length === 0) {
    return null;
  }

  return (
    <div className="site-banners" style={{ top: `${topOffset}px` }}>
      {visible.map((banner) => (
        <div
          key={banner.id}
          className="site-banner"
          style={{ backgroundColor: banner.color || "#5c4033" }}
          role="status"
        >
          <CampaignIcon className="site-banner__icon" />
          <div className="site-banner__text">
            {banner.title && (
              <div className="site-banner__title">{banner.title}</div>
            )}
            <div className="site-banner__content">{banner.content}</div>
          </div>
          {banner.link_url && (
            <Button
              className="site-banner__button"
              color="inherit"
              size="small"
              variant="outlined"
              href={banner.link_url}
              target={banner.link_url.startsWith("http") ? "_blank" : undefined}
              rel="noopener noreferrer"
            >
              {banner.link_label || "Zobacz"}
            </Button>
          )}
          {banner.dismissible && (
            <button
              type="button"
              className="site-banner__close"
              onClick={() => handleDismiss(banner.id)}
              aria-label="Zamknij baner"
            >
              <CloseIcon fontSize="small" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default SiteBanners;
